import React, { ReactNode, useRef, useLayoutEffect, KeyboardEvent } from 'react'
import cx from 'classnames'
import { Icon, type IconNamesEnum } from 'common/Icon'
import './style.sass'

interface getContentProps {
	children: ReactNode
	iconBefore?: IconNamesEnum
	iconAfter?: IconNamesEnum
	stroke?: string
}

const getContent = ({ children, iconBefore, iconAfter, stroke }: getContentProps) => {
	if (iconBefore || iconAfter) {
		return (
			<span className='Tab__content'>
				{iconBefore && <Icon className='Tab__content--before' name={iconBefore} width={16} height={16} stroke={stroke} />}
				{children}
				{iconAfter && <Icon className='Tab__content--after' name={iconAfter} width={16} height={16} stroke={stroke} />}
			</span>
		)
	}
	return children
}

type TabsProps = {
	children: ReactNode
	className?: string
	containerClassName?: string
	value: string
	onChange: (value: string) => void
	ariaLabel: string
}

export const Tabs = ({
	children,
	className,
	containerClassName,
	value,
	onChange,
	ariaLabel,
}: TabsProps): React.ReactElement<'div'> => {
	const containerClass: string = cx('Tabable', containerClassName)
	const tabs = useRef<NodeListOf<Element> | null>(null)
	const cs: string = cx('Tabs', className)
	const tabFocus = useRef(0)

	const clonedChildren = React.Children.map(children, child =>
		React.cloneElement(child as React.ReactElement<any>, {
			value: value,
			onChange: onChange,
		}),
	)

	useLayoutEffect(() => {
		if (tabs.current === null || tabs.current.length === 0) {
			tabs.current = document.querySelectorAll('[role="tab"]')

			for (let idx = 0; idx < tabs.current.length; idx++) {
				if (tabs.current[idx].id === value) {
					tabFocus.current = idx
					break
				}
			}
		}
	})

	const handleKeyDownEvent = (e: KeyboardEvent<HTMLDivElement>) => {
		// Move right
		if (e.keyCode === 39 || e.keyCode === 37) {
			if (tabs.current) {
				tabs.current[tabFocus.current].setAttribute('tabindex', '-1')
				if (e.keyCode === 39) {
					tabFocus.current++
					// If we're at the end, go to the start
					if (tabFocus.current >= tabs.current.length) {
						tabFocus.current = 0
					}
					// Move left
				} else if (e.keyCode === 37) {
					tabFocus.current--
					// If we're at the start, move to the end
					if (tabFocus.current < 0) {
						tabFocus.current = tabs.current.length - 1
					}
				}

				tabs.current[tabFocus.current].setAttribute('tabindex', '0')
				;(tabs.current[tabFocus.current] as HTMLElement).focus()
			}
		}
	}

	return (
		<div className={containerClass}>
			<div className={cs} role='tablist' aria-label={ariaLabel} onKeyDown={handleKeyDownEvent}>
				{clonedChildren}
			</div>
		</div>
	)
}

type SingleTabProps = {
	id: string
	onChange?: (value: string) => void
	className?: string
	clear?: boolean
	value?: string
	to?: string
	rest?: {
		[key: string]: string
	}
	children: ReactNode
	iconBefore?: IconNamesEnum
	iconAfter?: IconNamesEnum
	stroke?: string
}
export const Tab = ({
	id,
	onChange,
	className,
	children,
	clear,
	value,
	to,
	iconBefore,
	iconAfter,
	stroke,
	...rest
}: SingleTabProps): React.ReactElement<'button'> => {
	const cs: string = cx('Tab', className, { 'Tab--active': value === id, 'Tab--clear': Boolean(clear) })

	const onClick = () => {
		if (onChange) onChange(id)
	}

	return (
		<button
			{...rest}
			onClick={onClick}
			className={cs}
			role='tab'
			id={id}
			aria-selected={value === id}
			tabIndex={Number(value === id) - 1}>
			{getContent({ children, iconBefore, iconAfter, stroke })}
		</button>
	)
}

type TabPanelProps = {
	value: string
	tabPanelValue: string
	children: ReactNode
	variants?: Object
}

export const TabPanel = ({ tabPanelValue, value, children }: TabPanelProps): React.ReactElement<'div'> => {
	return (
		<div
			className='TabPanel__Container'
			role='tabpanel'
			tabIndex={Number(value === tabPanelValue) - 1}
			aria-labelledby={tabPanelValue}>
			{children}
		</div>
	)
}
